import React, { createContext, useContext, useReducer, useEffect, useCallback, useState } from 'react';
import {
  Account,
  Transaction,
  Category,
  TransactionFilters,
  CreateTransactionDTO,
  UpdateTransactionDTO,
  CreateAccountDTO,
  UpdateAccountDTO,
  CreateCategoryDTO,
  UpdateCategoryDTO,
  QuickFilter,
  SortField,
  SortDirection,
} from '../../types/budget';
import { budgetApi, TransactionWithRelations, BudgetShareReceived, ShareUser } from '../../services/budgetApi';

// ============================================================================
// State Types
// ============================================================================

interface BudgetState {
  accounts: Account[];
  transactions: TransactionWithRelations[];
  categories: Category[];
  filters: TransactionFilters;
  loading: boolean;
  error: string | null;
}

export interface AnalyticsAppliedFilter {
  source: 'monthly-spend-chart';
  year: number;
  monthIndex: number;
  categoryId: string;
  categoryName: string;
}

// ============================================================================
// Action Types
// ============================================================================

type BudgetAction =
  | { type: 'LOADING' }
  | { type: 'LOAD_SUCCESS'; accounts: Account[]; transactions: TransactionWithRelations[]; categories: Category[] }
  | { type: 'LOAD_ERROR'; error: string }
  | { type: 'SET_ACCOUNTS'; accounts: Account[] }
  | { type: 'ADD_ACCOUNT'; account: Account }
  | { type: 'UPDATE_ACCOUNT'; account: Account }
  | { type: 'DELETE_ACCOUNT'; id: string }
  | { type: 'SET_TRANSACTIONS'; transactions: TransactionWithRelations[] }
  | { type: 'ADD_TRANSACTION'; transaction: TransactionWithRelations }
  | { type: 'UPDATE_TRANSACTION'; transaction: TransactionWithRelations }
  | { type: 'DELETE_TRANSACTION'; id: string }
  | { type: 'BULK_DELETE_TRANSACTIONS'; ids: string[] }
  | { type: 'BULK_ADD_TRANSACTIONS'; transactions: TransactionWithRelations[] }
  | { type: 'SET_CATEGORIES'; categories: Category[] }
  | { type: 'ADD_CATEGORY'; category: Category }
  | { type: 'UPDATE_CATEGORY'; category: Category }
  | { type: 'DELETE_CATEGORY'; id: string }
  | { type: 'SET_FILTERS'; filters: Partial<TransactionFilters> }
  | { type: 'RESET_FILTERS' };

// ============================================================================
// Initial State
// ============================================================================

const defaultFilters: TransactionFilters = {
  quickFilter: 'uncategorized',
  categoryId: null,
  accountId: null,
  searchQuery: '',
  dateFrom: null,
  dateTo: null,
  sortField: 'date',
  sortDirection: 'desc',
};

const initialState: BudgetState = {
  accounts: [],
  transactions: [],
  categories: [],
  filters: defaultFilters,
  loading: false,
  error: null,
};

// ============================================================================
// Reducer
// ============================================================================

const budgetReducer = (state: BudgetState, action: BudgetAction): BudgetState => {
  switch (action.type) {
    case 'LOADING':
      return { ...state, loading: true, error: null };

    case 'LOAD_SUCCESS':
      return {
        ...state,
        accounts: action.accounts,
        transactions: action.transactions,
        categories: action.categories,
        loading: false,
        error: null,
      };

    case 'SET_ACCOUNTS':
      return {
        ...state,
        accounts: action.accounts,
      };

    case 'ADD_ACCOUNT':
      return {
        ...state,
        accounts: [...state.accounts, action.account],
      };

    case 'UPDATE_ACCOUNT':
      return {
        ...state,
        accounts: state.accounts.map(a =>
          a.id === action.account.id ? action.account : a
        ),
      };

    case 'DELETE_ACCOUNT':
      return {
        ...state,
        accounts: state.accounts.filter(a => a.id !== action.id),
      };

    case 'SET_TRANSACTIONS':
      return {
        ...state,
        transactions: action.transactions,
      };

    case 'LOAD_ERROR':
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case 'ADD_TRANSACTION':
      return {
        ...state,
        transactions: [...state.transactions, action.transaction],
      };

    case 'UPDATE_TRANSACTION':
      return {
        ...state,
        transactions: state.transactions.map(t =>
          t.id === action.transaction.id ? action.transaction : t
        ),
      };

    case 'DELETE_TRANSACTION':
      return {
        ...state,
        transactions: state.transactions.filter(t => t.id !== action.id),
      };

    case 'BULK_DELETE_TRANSACTIONS':
      const deleteSet = new Set(action.ids);
      return {
        ...state,
        transactions: state.transactions.filter(t => !deleteSet.has(t.id)),
      };

    case 'BULK_ADD_TRANSACTIONS':
      return {
        ...state,
        transactions: [...state.transactions, ...action.transactions],
      };

    case 'SET_CATEGORIES':
      return {
        ...state,
        categories: action.categories,
      };

    case 'ADD_CATEGORY':
      return {
        ...state,
        categories: [...state.categories, action.category],
      };

    case 'UPDATE_CATEGORY':
      return {
        ...state,
        categories: state.categories.map(c =>
          c.id === action.category.id ? action.category : c
        ),
      };

    case 'DELETE_CATEGORY':
      return {
        ...state,
        categories: state.categories.filter(c => c.id !== action.id),
      };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.filters },
      };

    case 'RESET_FILTERS':
      return {
        ...state,
        filters: defaultFilters,
      };

    default:
      return state;
  }
};

// ============================================================================
// Context
// ============================================================================

interface BudgetContextValue extends BudgetState {
  // Data loading
  loadData: () => Promise<void>;
  refreshAccounts: () => Promise<void>;
  refreshTransactions: () => Promise<void>;
  
  // Account helpers
  getAccountById: (id: string | null) => Account | null;
  activeAccounts: Account[];
  
  // Account operations
  createAccount: (dto: CreateAccountDTO) => Promise<Account>;
  updateAccount: (id: string, dto: UpdateAccountDTO) => Promise<Account>;
  deactivateAccount: (id: string) => Promise<Account>;
  reactivateAccount: (id: string) => Promise<Account>;
  deleteAccount: (id: string) => Promise<void>;
  
  // Transaction operations
  createTransaction: (dto: CreateTransactionDTO) => Promise<TransactionWithRelations>;
  updateTransaction: (id: string, dto: UpdateTransactionDTO) => Promise<TransactionWithRelations>;
  deleteTransaction: (id: string) => Promise<void>;
  bulkUpdateTransactions: (ids: string[], categoryId: string | null) => Promise<void>;
  bulkDeleteTransactions: (ids: string[]) => Promise<void>;
  importTransactions: (dtos: CreateTransactionDTO[], accountId?: string | null) => Promise<void>;
  
  // Category operations
  createCategory: (dto: CreateCategoryDTO) => Promise<Category>;
  updateCategory: (id: string, dto: UpdateCategoryDTO) => Promise<Category>;
  deleteCategory: (id: string) => Promise<void>;
  reorderCategories: (orderedIds: string[]) => Promise<void>;
  getCategoryById: (id: string | null) => Category | null;
  
  // Filter operations
  setFilters: (filters: Partial<TransactionFilters>) => void;
  setQuickFilter: (filter: QuickFilter) => void;
  setCategoryFilter: (categoryId: string | null) => void;
  setAccountFilter: (accountId: string | null) => void;
  setSearchQuery: (query: string) => void;
  setDateRange: (from: string | null, to: string | null) => void;
  setSort: (field: SortField, direction: SortDirection) => void;
  resetFilters: () => void;

  // Analytics -> Transactions linkage
  analyticsAppliedFilter: AnalyticsAppliedFilter | null;
  applyFiltersFromAnalytics: (nextFilters: TransactionFilters, info: AnalyticsAppliedFilter) => void;
  clearAnalyticsAppliedFilter: () => void;
  
  // Filtered/sorted transactions
  filteredTransactions: TransactionWithRelations[];
  
  // Bulk selection
  isSelectMode: boolean;
  selectedIds: Set<string>;
  enterSelectMode: () => void;
  exitSelectMode: () => void;
  toggleSelection: (id: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  
  // Creation mode
  isCreating: boolean;
  setIsCreating: (value: boolean) => void;
  
  // Viewing mode (for shared budgets)
  viewingUserId: string | null;
  viewingUserName: string | null;
  isReadOnly: boolean;
  sharedBudgets: BudgetShareReceived[];
  setViewingUser: (userId: string | null, userName: string | null) => void;
  refreshSharedBudgets: () => Promise<void>;
}

const BudgetContext = createContext<BudgetContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export const BudgetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(budgetReducer, initialState);
  
  // Bulk selection state
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  // Creation mode state
  const [isCreating, setIsCreating] = useState(false);
  
  // Viewing mode state (for shared budgets)
  const [viewingUserId, setViewingUserId] = useState<string | null>(null);
  const [viewingUserName, setViewingUserName] = useState<string | null>(null);
  const [sharedBudgets, setSharedBudgets] = useState<BudgetShareReceived[]>([]);

  // Analytics -> Transactions linkage
  const [analyticsAppliedFilter, setAnalyticsAppliedFilter] = useState<AnalyticsAppliedFilter | null>(null);
  
  // Computed read-only flag
  const isReadOnly = viewingUserId !== null;

  // -------------------------------------------------------------------------
  // Shared Budgets
  // -------------------------------------------------------------------------
  
  const refreshSharedBudgets = useCallback(async () => {
    try {
      const shares = await budgetApi.sharing.getSharesReceived();
      setSharedBudgets(shares);
    } catch (error) {
      console.error('Failed to load shared budgets:', error);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Viewing Mode
  // -------------------------------------------------------------------------
  
  const setViewingUser = useCallback((userId: string | null, userName: string | null) => {
    setViewingUserId(userId);
    setViewingUserName(userName);
    // Reset selection when switching budgets
    setSelectedIds(new Set());
    setIsSelectMode(false);
    setAnalyticsAppliedFilter(null);
  }, []);

  // -------------------------------------------------------------------------
  // Data Loading
  // -------------------------------------------------------------------------

  const loadData = useCallback(async () => {
    dispatch({ type: 'LOADING' });
    try {
      const [accounts, transactions, categories] = await Promise.all([
        budgetApi.accounts.getAll(false, viewingUserId || undefined),
        budgetApi.transactions.getAll(undefined, viewingUserId || undefined),
        budgetApi.categories.getAll(viewingUserId || undefined),
      ]);
      
      // If no categories exist and viewing own budget, initialize defaults
      let finalCategories = categories;
      if (categories.length === 0 && !viewingUserId) {
        finalCategories = await budgetApi.categories.initializeDefaults();
      }
      
      dispatch({ type: 'LOAD_SUCCESS', accounts, transactions, categories: finalCategories });
    } catch (error) {
      console.error('Failed to load budget data:', error);
      dispatch({ type: 'LOAD_ERROR', error: 'Failed to load data' });
    }
  }, [viewingUserId]);

  const refreshAccounts = useCallback(async () => {
    try {
      const accounts = await budgetApi.accounts.getAll(false, viewingUserId || undefined);
      dispatch({ type: 'SET_ACCOUNTS', accounts });
    } catch (error) {
      console.error('Failed to refresh accounts:', error);
    }
  }, [viewingUserId]);

  const refreshTransactions = useCallback(async () => {
    try {
      const transactions = await budgetApi.transactions.getAll(undefined, viewingUserId || undefined);
      dispatch({ type: 'SET_TRANSACTIONS', transactions });
    } catch (error) {
      console.error('Failed to refresh transactions:', error);
    }
  }, [viewingUserId]);

  // Load data on mount and when viewingUserId changes
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Load shared budgets on mount
  useEffect(() => {
    refreshSharedBudgets();
  }, [refreshSharedBudgets]);

  // -------------------------------------------------------------------------
  // Transaction Operations
  // -------------------------------------------------------------------------

  const createTransaction = useCallback(async (dto: CreateTransactionDTO): Promise<TransactionWithRelations> => {
    const transaction = await budgetApi.transactions.create(dto);
    dispatch({ type: 'ADD_TRANSACTION', transaction });
    return transaction;
  }, []);

  const updateTransaction = useCallback(async (id: string, dto: UpdateTransactionDTO): Promise<TransactionWithRelations> => {
    const transaction = await budgetApi.transactions.update(id, dto);
    dispatch({ type: 'UPDATE_TRANSACTION', transaction });
    return transaction;
  }, []);

  const deleteTransaction = useCallback(async (id: string): Promise<void> => {
    await budgetApi.transactions.delete(id);
    dispatch({ type: 'DELETE_TRANSACTION', id });
  }, []);

  const bulkUpdateTransactions = useCallback(async (ids: string[], categoryId: string | null): Promise<void> => {
    await budgetApi.transactions.bulkUpdate(ids, categoryId);
    // Refresh transactions to get updated data with relations
    await refreshTransactions();
    // Clear selection after bulk operation
    setSelectedIds(new Set());
    setIsSelectMode(false);
  }, [refreshTransactions]);

  const bulkDeleteTransactions = useCallback(async (ids: string[]): Promise<void> => {
    await budgetApi.transactions.bulkDelete(ids);
    dispatch({ type: 'BULK_DELETE_TRANSACTIONS', ids });
    // Clear selection after bulk operation
    setSelectedIds(new Set());
    setIsSelectMode(false);
  }, []);

  const importTransactions = useCallback(async (dtos: CreateTransactionDTO[], accountId?: string | null): Promise<void> => {
    await budgetApi.transactions.bulkCreate(dtos, accountId || undefined);
    // Refresh transactions to get all new data with relations
    await refreshTransactions();
  }, [refreshTransactions]);

  // -------------------------------------------------------------------------
  // Account Helpers
  // -------------------------------------------------------------------------

  const getAccountById = useCallback((id: string | null): Account | null => {
    if (!id) return null;
    return state.accounts.find(a => a.id === id) || null;
  }, [state.accounts]);

  const activeAccounts = React.useMemo(() => {
    return state.accounts.filter(a => a.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  }, [state.accounts]);

  // -------------------------------------------------------------------------
  // Account Operations
  // -------------------------------------------------------------------------

  const createAccount = useCallback(async (dto: CreateAccountDTO): Promise<Account> => {
    const account = await budgetApi.accounts.create(dto);
    dispatch({ type: 'ADD_ACCOUNT', account });
    return account;
  }, []);

  const updateAccount = useCallback(async (id: string, dto: UpdateAccountDTO): Promise<Account> => {
    const account = await budgetApi.accounts.update(id, dto);
    dispatch({ type: 'UPDATE_ACCOUNT', account });
    return account;
  }, []);

  const deactivateAccount = useCallback(async (id: string): Promise<Account> => {
    const account = await budgetApi.accounts.deactivate(id);
    dispatch({ type: 'UPDATE_ACCOUNT', account });
    return account;
  }, []);

  const reactivateAccount = useCallback(async (id: string): Promise<Account> => {
    const account = await budgetApi.accounts.reactivate(id);
    dispatch({ type: 'UPDATE_ACCOUNT', account });
    return account;
  }, []);

  const deleteAccount = useCallback(async (id: string): Promise<void> => {
    await budgetApi.accounts.delete(id);
    dispatch({ type: 'DELETE_ACCOUNT', id });
  }, []);

  // -------------------------------------------------------------------------
  // Category Operations
  // -------------------------------------------------------------------------

  const createCategory = useCallback(async (dto: CreateCategoryDTO): Promise<Category> => {
    const category = await budgetApi.categories.create(dto);
    dispatch({ type: 'ADD_CATEGORY', category });
    return category;
  }, []);

  const updateCategory = useCallback(async (id: string, dto: UpdateCategoryDTO): Promise<Category> => {
    const category = await budgetApi.categories.update(id, dto);
    dispatch({ type: 'UPDATE_CATEGORY', category });
    return category;
  }, []);

  const deleteCategory = useCallback(async (id: string): Promise<void> => {
    await budgetApi.categories.delete(id);
    dispatch({ type: 'DELETE_CATEGORY', id });
    // Refresh transactions since some may have been uncategorized
    await refreshTransactions();
  }, [refreshTransactions]);

  const reorderCategories = useCallback(async (orderedIds: string[]): Promise<void> => {
    const categories = await budgetApi.categories.reorder(orderedIds);
    dispatch({ type: 'SET_CATEGORIES', categories });
  }, []);

  const getCategoryById = useCallback((id: string | null): Category | null => {
    if (!id) return null;
    return state.categories.find(c => c.id === id) || null;
  }, [state.categories]);

  // -------------------------------------------------------------------------
  // Filter Operations
  // -------------------------------------------------------------------------

  const setFilters = useCallback((filters: Partial<TransactionFilters>) => {
    setAnalyticsAppliedFilter(null);
    dispatch({ type: 'SET_FILTERS', filters });
  }, []);

  const setQuickFilter = useCallback((filter: QuickFilter) => {
    setAnalyticsAppliedFilter(null);
    dispatch({ type: 'SET_FILTERS', filters: { quickFilter: filter, categoryId: null } });
  }, []);

  const setCategoryFilter = useCallback((categoryId: string | null) => {
    setAnalyticsAppliedFilter(null);
    dispatch({ type: 'SET_FILTERS', filters: { categoryId, quickFilter: 'all' } });
  }, []);

  const setAccountFilter = useCallback((accountId: string | null) => {
    setAnalyticsAppliedFilter(null);
    dispatch({ type: 'SET_FILTERS', filters: { accountId } });
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setAnalyticsAppliedFilter(null);
    dispatch({ type: 'SET_FILTERS', filters: { searchQuery: query } });
  }, []);

  const setDateRange = useCallback((from: string | null, to: string | null) => {
    setAnalyticsAppliedFilter(null);
    dispatch({ type: 'SET_FILTERS', filters: { dateFrom: from, dateTo: to } });
  }, []);

  const setSort = useCallback((field: SortField, direction: SortDirection) => {
    setAnalyticsAppliedFilter(null);
    dispatch({ type: 'SET_FILTERS', filters: { sortField: field, sortDirection: direction } });
  }, []);

  const resetFilters = useCallback(() => {
    setAnalyticsAppliedFilter(null);
    dispatch({ type: 'RESET_FILTERS' });
  }, []);

  const clearAnalyticsAppliedFilter = useCallback(() => {
    setAnalyticsAppliedFilter(null);
  }, []);

  const applyFiltersFromAnalytics = useCallback((nextFilters: TransactionFilters, info: AnalyticsAppliedFilter) => {
    // Replace all filter fields so the transactions view exactly matches what the user clicked.
    dispatch({
      type: 'SET_FILTERS',
      filters: {
        quickFilter: nextFilters.quickFilter,
        categoryId: nextFilters.categoryId,
        accountId: nextFilters.accountId,
        searchQuery: nextFilters.searchQuery,
        dateFrom: nextFilters.dateFrom,
        dateTo: nextFilters.dateTo,
        sortField: nextFilters.sortField,
        sortDirection: nextFilters.sortDirection,
      },
    });
    setAnalyticsAppliedFilter(info);
  }, []);

  // -------------------------------------------------------------------------
  // Filtered Transactions
  // -------------------------------------------------------------------------

  const filteredTransactions = React.useMemo(() => {
    let result = [...state.transactions];
    const { filters } = state;

    const getDateKey = (dateStr: string): string => {
      // Supports both "YYYY-MM-DD" and full ISO timestamps by comparing on the leading date portion.
      return (dateStr || '').slice(0, 10);
    };

    // Apply quick filter
    switch (filters.quickFilter) {
      case 'uncategorized':
        result = result.filter(t => t.categoryId === null);
        break;
      case 'expenses':
        result = result.filter(t => Number(t.amount) < 0);
        break;
      case 'income':
        result = result.filter(t => Number(t.amount) > 0);
        break;
      // 'all' - no filter
    }

    // Apply category filter (if set and not using quick filter)
    if (filters.categoryId && filters.quickFilter === 'all') {
      result = result.filter(t => t.categoryId === filters.categoryId);
    }

    // Apply account filter
    if (filters.accountId) {
      result = result.filter(t => t.accountId === filters.accountId);
    }

    // Apply search filter (description or amount)
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase().trim();
      const queryAsNumber = parseFloat(filters.searchQuery);
      result = result.filter(t => {
        // Match description (case-insensitive substring)
        const matchesDescription = t.description.toLowerCase().includes(query);
        // Match amount (exact match on absolute value)
        const matchesAmount = !isNaN(queryAsNumber) && Math.abs(Number(t.amount)) === Math.abs(queryAsNumber);
        return matchesDescription || matchesAmount;
      });
    }

    // Apply date range filter
    if (filters.dateFrom) {
      result = result.filter(t => getDateKey(t.date) >= filters.dateFrom!);
    }
    if (filters.dateTo) {
      result = result.filter(t => getDateKey(t.date) <= filters.dateTo!);
    }

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;

      switch (filters.sortField) {
        case 'date':
          comparison = a.date.localeCompare(b.date);
          break;
        case 'amount':
          comparison = Number(a.amount) - Number(b.amount);
          break;
        case 'description':
          comparison = a.description.localeCompare(b.description);
          break;
      }

      return filters.sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [state.transactions, state.filters]);

  // -------------------------------------------------------------------------
  // Bulk Selection
  // -------------------------------------------------------------------------

  const enterSelectMode = useCallback(() => {
    setIsSelectMode(true);
  }, []);

  const exitSelectMode = useCallback(() => {
    setIsSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(filteredTransactions.map(t => t.id)));
  }, [filteredTransactions]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // -------------------------------------------------------------------------
  // Context Value
  // -------------------------------------------------------------------------

  const contextValue: BudgetContextValue = {
    ...state,
    loadData,
    refreshAccounts,
    refreshTransactions,
    getAccountById,
    activeAccounts,
    createAccount,
    updateAccount,
    deactivateAccount,
    reactivateAccount,
    deleteAccount,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    bulkUpdateTransactions,
    bulkDeleteTransactions,
    importTransactions,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    getCategoryById,
    setFilters,
    setQuickFilter,
    setCategoryFilter,
    setAccountFilter,
    setSearchQuery,
    setDateRange,
    setSort,
    resetFilters,
    analyticsAppliedFilter,
    applyFiltersFromAnalytics,
    clearAnalyticsAppliedFilter,
    filteredTransactions,
    isSelectMode,
    selectedIds,
    enterSelectMode,
    exitSelectMode,
    toggleSelection,
    selectAll,
    clearSelection,
    isCreating,
    setIsCreating,
    // Viewing mode (for shared budgets)
    viewingUserId,
    viewingUserName,
    isReadOnly,
    sharedBudgets,
    setViewingUser,
    refreshSharedBudgets,
  };

  return (
    <BudgetContext.Provider value={contextValue}>
      {children}
    </BudgetContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export const useBudget = (): BudgetContextValue => {
  const context = useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudget must be used within a BudgetProvider');
  }
  return context;
};
